/**
 * Invoice / Receipt — print-ready, presentational only.
 *
 * Layouts (via `format`):
 *   "a4"      → standard printer / PDF
 *   "thermal" → 80mm thermal receipt printer
 *
 * Document kinds (via `kind`):
 *   "order"      + order object   → sales invoice
 *   "collection" + payment object → payment receipt
 *
 * Company branding comes from the `company` prop (real Odoo company, fetched
 * by the parent via useCompanyInfo). Falls back to the static COMPANY config.
 *
 * Parent loads the data; this component just renders it.
 */
import { COMPANY, formatMoney, formatDateTime } from '@/lib/companyConfig';
import { companyAddress } from '@/lib/useCompanyInfo';

// Odoo may store the order note as HTML (e.g. wrapped in <p>…</p>). Strip the
// tags and decode the common entities so the printed note reads as plain text.
function cleanNote(note) {
  if (!note) return '';
  return String(note)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function metaRow(label, value) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between gap-4 py-0.5 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}

function readCustomer(doc) {
  const c = doc.partner_id || doc.customer || {};
  if (Array.isArray(c)) return { name: c[1] || '—', phone: '', address: '' };
  return {
    name: c.name || c.display_name || doc.customer_name || '—',
    phone: c.phone || c.mobile || doc.customer_phone || '',
    address: c.street || c.address || '',
  };
}

function readLines(order) {
  const lines = order.order_line || order.lines || [];
  return lines.map((l) => ({
    name: Array.isArray(l.product_id) ? l.product_id[1] : (l.name || l.product_name || 'منتج'),
    qty: Number(l.product_uom_qty ?? l.quantity ?? l.qty ?? 1),
    price: Number(l.price_unit ?? l.price ?? 0),
    total: Number(l.price_subtotal ?? (Number(l.product_uom_qty ?? l.quantity ?? 1) * Number(l.price_unit ?? l.price ?? 0))),
  }));
}

function readTotals(order) {
  const subtotal = Number(order.amount_untaxed ?? order.subtotal ?? 0);
  const tax = Number(order.amount_tax ?? order.tax ?? 0);
  const total = Number(order.amount_total ?? order.total ?? subtotal + tax);
  return { subtotal, tax, total };
}

// ── A4 — sale order ──────────────────────────────────────────────────────
function A4Order({ order, company }) {
  const customer = readCustomer(order);
  const lines = readLines(order);
  const { subtotal, tax, total } = readTotals(order);
  const salesman = Array.isArray(order.user_id) ? order.user_id[1] : (order.salesman_name || '');

  return (
    <div className="invoice-doc bg-white text-gray-900 mx-auto p-8" style={{ width: '210mm', minHeight: '297mm' }} dir="rtl">
      <header className="flex justify-between items-start pb-6 border-b-4 border-primary">
        <div className="flex items-start gap-4">
          {company.logoUrl && <img src={company.logoUrl} alt="" className="w-20 h-20 rounded-2xl flex-shrink-0 object-contain" />}
          <div>
            <h1 className="text-2xl font-black leading-tight">{company.name}</h1>
            {company.legalForm && <p className="text-xs text-gray-500 mt-1">{company.legalForm}</p>}
            {companyAddress(company) && <p className="text-xs text-gray-600 mt-1.5">{companyAddress(company)}</p>}
            {company.phone && <p className="text-xs text-gray-600">هاتف: {company.phone}{company.mobile ? ` • جوال: ${company.mobile}` : ''}</p>}
            {(company.taxId || company.commercialReg) && (
              <p className="text-xs text-gray-600">
                {[
                  company.taxId ? `الرقم الضريبي: ${company.taxId}` : '',
                  company.commercialReg ? `س.ت: ${company.commercialReg}` : '',
                ].filter(Boolean).join(' • ')}
              </p>
            )}
          </div>
        </div>
        <div className="text-left flex-shrink-0">
          <h2 className="text-3xl font-black text-primary">فاتورة</h2>
          <p className="text-[11px] text-gray-400 tracking-widest mt-1">TAX INVOICE</p>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-8 mt-6 pb-5 border-b border-gray-200">
        <div>
          <p className="text-[11px] font-bold text-gray-400 uppercase mb-2">فاتورة إلى</p>
          <p className="font-bold text-lg">{customer.name}</p>
          {customer.phone && <p className="text-sm text-gray-600">{customer.phone}</p>}
          {customer.address && <p className="text-sm text-gray-600">{customer.address}</p>}
        </div>
        <div>
          <p className="text-[11px] font-bold text-gray-400 uppercase mb-2">تفاصيل الفاتورة</p>
          {metaRow('رقم الفاتورة', order.name || `#${order.id}`)}
          {metaRow('التاريخ', formatDateTime(order.date_order || order.created_date || new Date()))}
          {metaRow('المندوب', salesman)}
        </div>
      </section>

      <section className="mt-6">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-primary text-white">
              <th className="p-2.5 text-right font-bold rounded-tr-lg w-10">#</th>
              <th className="p-2.5 text-right font-bold">الصنف</th>
              <th className="p-2.5 text-center font-bold w-20">الكمية</th>
              <th className="p-2.5 text-center font-bold w-28">سعر الوحدة</th>
              <th className="p-2.5 text-left font-bold w-32 rounded-tl-lg">الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className={i % 2 ? 'bg-gray-50' : 'bg-white'}>
                <td className="p-2.5 text-gray-400 border-b border-gray-100">{i + 1}</td>
                <td className="p-2.5 border-b border-gray-100">{l.name}</td>
                <td className="p-2.5 text-center border-b border-gray-100">{l.qty}</td>
                <td className="p-2.5 text-center border-b border-gray-100">{formatMoney(l.price, { withSymbol: false })}</td>
                <td className="p-2.5 text-left font-semibold border-b border-gray-100">{formatMoney(l.total, { withSymbol: false })}</td>
              </tr>
            ))}
            {lines.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-gray-400">لا توجد بنود</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="mt-6 flex justify-end">
        <div className="w-80 space-y-1.5">
          {metaRow('الإجمالي قبل الضريبة', formatMoney(subtotal))}
          {tax > 0 && metaRow(`ضريبة القيمة المضافة (${(company.vatRate * 100).toFixed(0)}%)`, formatMoney(tax))}
          <div className="flex justify-between items-center bg-primary text-white px-4 py-3 rounded-xl mt-2">
            <span className="font-bold">الإجمالي المستحق</span>
            <span className="font-black text-xl">{formatMoney(total)}</span>
          </div>
        </div>
      </section>

      {cleanNote(order.note) && (
        <section className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-[11px] font-bold text-gray-400 uppercase mb-1">ملاحظات</p>
          <p className="text-sm text-gray-700 whitespace-pre-line">{cleanNote(order.note)}</p>
        </section>
      )}

      <footer className="mt-10 pt-5 border-t border-gray-300 text-center text-xs text-gray-500 space-y-1">
        <p className="font-bold text-gray-800 text-sm">{company.receipt.thankYouMessage}</p>
        <p>{company.receipt.returnPolicy}</p>
        {(company.website || company.email) && (
          <p className="text-gray-400 mt-2">{[company.website, company.email].filter(Boolean).join(' • ')}</p>
        )}
      </footer>
    </div>
  );
}

// ── A4 — collection receipt ────────────────────────────────────────────
function A4Collection({ collection, company }) {
  const customer = readCustomer(collection);
  const amount = Number(collection.amount ?? collection.total ?? 0);
  const salesman = collection.salesman_name || (Array.isArray(collection.user_id) ? collection.user_id[1] : '');

  return (
    <div className="invoice-doc bg-white text-gray-900 mx-auto p-8" style={{ width: '210mm', minHeight: '297mm' }} dir="rtl">
      <header className="flex justify-between items-start pb-6 border-b-4 border-emerald-600">
        <div className="flex items-start gap-4">
          {company.logoUrl && <img src={company.logoUrl} alt="" className="w-20 h-20 rounded-2xl flex-shrink-0 object-contain" />}
          <div>
            <h1 className="text-2xl font-black leading-tight">{company.name}</h1>
            {companyAddress(company) && <p className="text-xs text-gray-600 mt-1.5">{companyAddress(company)}</p>}
            {company.phone && <p className="text-xs text-gray-600">هاتف: {company.phone}{company.commercialReg ? ` • س.ت: ${company.commercialReg}` : ''}</p>}
          </div>
        </div>
        <div className="text-left flex-shrink-0">
          <h2 className="text-3xl font-black text-emerald-600">سند قبض</h2>
          <p className="text-[11px] text-gray-400 tracking-widest mt-1">PAYMENT RECEIPT</p>
        </div>
      </header>

      <section className="mt-8 space-y-6">
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-8 text-center">
          <p className="text-sm text-gray-500 mb-2">المبلغ المستلم</p>
          <p className="text-5xl font-black text-emerald-700">{formatMoney(amount)}</p>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-2">استلمنا من</p>
            <p className="font-bold text-xl">{customer.name}</p>
            {customer.phone && <p className="text-sm text-gray-600">{customer.phone}</p>}
          </div>
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-2">تفاصيل السند</p>
            {metaRow('رقم السند', collection.name || `#${collection.id}`)}
            {metaRow('التاريخ', formatDateTime(collection.date || collection.created_date || new Date()))}
            {metaRow('طريقة الدفع', collection.payment_method || collection.method || 'نقدي')}
            {metaRow('المندوب', salesman)}
          </div>
        </div>

        {collection.notes && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-1">ملاحظات</p>
            <p className="text-sm text-gray-700">{collection.notes}</p>
          </div>
        )}
      </section>

      <section className="mt-16 grid grid-cols-2 gap-16">
        <div className="text-center">
          <div className="border-t border-gray-400 pt-2 text-xs text-gray-500">توقيع المستلم</div>
        </div>
        <div className="text-center">
          <div className="border-t border-gray-400 pt-2 text-xs text-gray-500">توقيع المحاسب</div>
        </div>
      </section>

      <footer className="mt-12 pt-5 border-t border-gray-300 text-center text-xs text-gray-500">
        <p className="font-bold text-gray-800 text-sm">{company.receipt.thankYouMessage}</p>
        {company.website && <p className="text-gray-400 mt-1">{company.website}</p>}
      </footer>
    </div>
  );
}

// ── Thermal 80mm — sale order ──────────────────────────────────────────
function ThermalOrder({ order, company }) {
  const customer = readCustomer(order);
  const lines = readLines(order);
  const { subtotal, tax, total } = readTotals(order);

  return (
    <div className="invoice-doc thermal-doc bg-white text-black mx-auto px-2 py-3"
         style={{ width: '80mm', fontFamily: "'Courier New', monospace" }} dir="rtl">
      <div className="text-center">
        {company.logoUrl && <img src={company.logoUrl} alt="" className="w-14 h-14 mx-auto mb-1 object-contain" />}
        <p className="font-bold text-base leading-tight">{company.name}</p>
        {[company.address.city, company.address.country].filter(Boolean).length > 0 && (
          <p className="text-[10px] leading-tight">{[company.address.city, company.address.country].filter(Boolean).join('، ')}</p>
        )}
        {company.phone && <p className="text-[10px]">هاتف: {company.phone}</p>}
        {company.taxId && <p className="text-[10px]">الرقم الضريبي: {company.taxId}</p>}
      </div>

      <div className="border-t border-dashed border-black my-2" />

      <p className="text-center font-bold text-sm">فاتورة مبيعات</p>
      <div className="text-[11px] mt-1 space-y-0.5">
        <div className="flex justify-between"><span>رقم:</span><span>{order.name || order.id}</span></div>
        <div className="flex justify-between"><span>التاريخ:</span><span>{formatDateTime(order.date_order || new Date())}</span></div>
        <div className="flex justify-between"><span>العميل:</span><span className="font-bold">{customer.name}</span></div>
      </div>

      <div className="border-t border-dashed border-black my-2" />

      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-black">
            <th className="text-right py-0.5">الصنف</th>
            <th className="text-center py-0.5 w-8">كمية</th>
            <th className="text-center py-0.5 w-14">سعر الوحدة</th>
            <th className="text-left py-0.5 w-14">إجمالي</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td className="text-right py-0.5 align-top">{l.name}</td>
              <td className="text-center py-0.5 align-top">{l.qty}</td>
              <td className="text-center py-0.5 align-top">{formatMoney(l.price, { withSymbol: false })}</td>
              <td className="text-left py-0.5 align-top">{formatMoney(l.total, { withSymbol: false })}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t border-dashed border-black my-2" />

      <div className="text-[11px] space-y-0.5">
        <div className="flex justify-between"><span>قبل الضريبة:</span><span>{formatMoney(subtotal)}</span></div>
        {tax > 0 && <div className="flex justify-between"><span>ضريبة {(company.vatRate * 100).toFixed(0)}%:</span><span>{formatMoney(tax)}</span></div>}
        <div className="flex justify-between font-bold text-sm border-t border-black pt-1 mt-1">
          <span>الإجمالي:</span><span>{formatMoney(total)}</span>
        </div>
      </div>

      {cleanNote(order.note) && (
        <>
          <div className="border-t border-dashed border-black my-2" />
          <div className="text-[10px] whitespace-pre-line">{cleanNote(order.note)}</div>
        </>
      )}

      <div className="border-t border-dashed border-black my-2" />

      <div className="text-center text-[10px] space-y-0.5">
        <p className="font-bold">{company.receipt.thankYouMessage}</p>
        {company.website && <p>{company.website}</p>}
      </div>
    </div>
  );
}

// ── Thermal 80mm — collection ──────────────────────────────────────────
function ThermalCollection({ collection, company }) {
  const customer = readCustomer(collection);
  const amount = Number(collection.amount ?? collection.total ?? 0);

  return (
    <div className="invoice-doc thermal-doc bg-white text-black mx-auto px-2 py-3"
         style={{ width: '80mm', fontFamily: "'Courier New', monospace" }} dir="rtl">
      <div className="text-center">
        {company.logoUrl && <img src={company.logoUrl} alt="" className="w-14 h-14 mx-auto mb-1 object-contain" />}
        <p className="font-bold text-base leading-tight">{company.name}</p>
        {company.phone && <p className="text-[10px]">هاتف: {company.phone}</p>}
      </div>

      <div className="border-t border-dashed border-black my-2" />

      <p className="text-center font-bold text-sm">سند قبض</p>

      <div className="text-center my-3">
        <p className="text-[10px]">المبلغ المستلم</p>
        <p className="text-2xl font-black">{formatMoney(amount)}</p>
      </div>

      <div className="text-[11px] space-y-0.5">
        <div className="flex justify-between"><span>رقم السند:</span><span>{collection.name || collection.id}</span></div>
        <div className="flex justify-between"><span>التاريخ:</span><span>{formatDateTime(collection.date || new Date())}</span></div>
        <div className="flex justify-between"><span>من:</span><span className="font-bold">{customer.name}</span></div>
        <div className="flex justify-between"><span>طريقة الدفع:</span><span>{collection.payment_method || 'نقدي'}</span></div>
      </div>

      <div className="border-t border-dashed border-black my-2" />

      <div className="text-center text-[10px]">
        <p className="font-bold">{company.receipt.thankYouMessage}</p>
      </div>
    </div>
  );
}

// ── Public component ─────────────────────────────────────────────────────
export default function InvoicePrint({ kind = 'order', data, format = 'a4', company = COMPANY }) {
  if (!data) return null;
  if (format === 'thermal') {
    return kind === 'collection'
      ? <ThermalCollection collection={data} company={company} />
      : <ThermalOrder order={data} company={company} />;
  }
  return kind === 'collection'
    ? <A4Collection collection={data} company={company} />
    : <A4Order order={data} company={company} />;
}
